// Collezioni della bottega: elenco (/collezioni) e pagina della singola collezione (/collezioni/:slug)
import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useShop } from '../ShopContext';
import ShopProductCard, { productImage } from '../ShopProductCard';
import { GhostLink, PageHeader, Reveal, Section } from '../ShopUi';

const count = (n) => `${n} ${n === 1 ? 'creazione' : 'creazioni'}`;

// Riquadro di una collezione (usato anche in home e nella vetrina del marketplace)
export function CollectionTile({ collection, to, large = false, className = '' }) {
  const { style } = useShop();
  const src = productImage(collection.cover, large ? 1000 : 700);
  return (
    <Link
      to={to}
      className={`group relative block overflow-hidden bg-slate-200 ${style.media} ${large ? 'aspect-[4/3] md:aspect-[21/9]' : 'aspect-[4/5]'} ${style.id === 'contemporaneo' ? 'border-2 border-slate-900' : ''} ${className}`}
      data-testid="shop-collection-tile"
    >
      {src && <img src={src} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}
      <span className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-transparent" />
      <span className={`absolute inset-x-0 bottom-0 p-5 text-white ${style.centered ? 'text-center' : ''}`}>
        <span className="text-xs uppercase tracking-[0.2em] text-white/75">Collezione · {count(collection.products.length)}</span>
        <span className={`block mt-1 ${style.heading} ${large ? 'text-2xl md:text-3xl' : 'text-xl md:text-2xl'}`}>{collection.title}</span>
        {large && collection.description && <span className="block mt-2 text-sm text-white/85 line-clamp-2 max-w-xl">{collection.description}</span>}
        <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium opacity-90 group-hover:gap-2 transition-all">Scopri <ArrowRight className="w-4 h-4" /></span>
      </span>
    </Link>
  );
}

export default function ShopCollections() {
  const { collections, name, paths } = useShop();
  const [first, ...rest] = collections;
  return (
    <div data-testid="shop-collections">
      <PageHeader title="Collezioni" subtitle={`Le creazioni di ${name} raccolte per tema, stagione o ispirazione.`} />
      <Section className="!pt-2">
        {first && (
          <Reveal>
            <CollectionTile collection={first} to={paths.page(`collezioni/${first.slug}`)} large />
          </Reveal>
        )}
        {rest.length > 0 && (
          <div className={`mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6 ${rest.length === 2 || rest.length === 4 ? '' : 'lg:grid-cols-3'}`}>
            {rest.map((c, i) => (
              <Reveal key={c.id} delay={i * 0.05}>
                <CollectionTile collection={c} to={paths.page(`collezioni/${c.slug}`)} />
              </Reveal>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

export function ShopCollection() {
  const { collectionSlug } = useParams();
  const { collections, paths, style } = useShop();
  const collection = collections.find((c) => c.slug === collectionSlug);
  if (!collection) return <Navigate to={paths.page('collezioni')} replace />;
  const others = collections.filter((c) => c.id !== collection.id).slice(0, 3);

  return (
    <div data-testid="shop-collection" data-collection={collection.slug}>
      <div className={`max-w-6xl mx-auto px-4 pt-8 ${style.centered ? 'text-center' : ''}`}>
        <Link to={paths.page('collezioni')}>
          <GhostLink><ArrowLeft className="w-4 h-4" /> Tutte le collezioni</GhostLink>
        </Link>
      </div>
      <PageHeader title={collection.title} subtitle={collection.description || null} />
      <div className={`max-w-6xl mx-auto px-4 -mt-2 mb-8 text-sm text-slate-500 ${style.centered ? 'text-center' : ''}`}>
        {count(collection.products.length)}
      </div>
      <div className="max-w-6xl mx-auto px-4">
        <div className={style.grid}>
          {collection.products.map((p) => <ShopProductCard key={p.id} product={p} />)}
        </div>
      </div>

      {others.length > 0 && (
        <Section title="Altre collezioni">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {others.map((c) => <CollectionTile key={c.id} collection={c} to={paths.page(`collezioni/${c.slug}`)} />)}
          </div>
        </Section>
      )}
    </div>
  );
}
